import { BaseFeature } from '../../core/base-feature.js';

/**
 * PiShockFeature - Frontend feature for PiShock device control
 * Handles: configuration, shocker management, shock/vibrate/beep commands
 */
export class PiShockFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.configured = false;
        this.shockers = [];
        this.recentEvents = [];

        // Event callbacks for React components
        this.shockEventCallbacks = new Set();
        this.statusUpdateCallbacks = new Set();
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'pishock',
            name: 'PiShock',
            version: 'v1.0',
            capabilities: [
                'configure',
                'manage_shockers',
                'shock',
                'vibrate',
                'beep',
                'get_status'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[PiShock] Initializing...');
        console.log('[PiShock] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;

        switch (command) {
            // Real-time updates
            case 'shock_event':
                return this.handleShockEvent(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            case 'status_update':
                return this.handleStatusUpdate(payload);

            // Responses to commands
            case 'configure_response':
                return this.handleResponse('configure', payload);

            case 'get_config_response':
                return this.handleResponse('get_config', payload);

            case 'add_shocker_response':
                return this.handleResponse('add_shocker', payload);

            case 'remove_shocker_response':
                return this.handleResponse('remove_shocker', payload);

            case 'get_shockers_response':
                return this.handleResponse('get_shockers', payload);

            case 'get_shocker_info_response':
                return this.handleResponse('get_shocker_info', payload);

            case 'shock_response':
                return this.handleResponse('shock', payload);

            case 'vibrate_response':
                return this.handleResponse('vibrate', payload);

            case 'beep_response':
                return this.handleResponse('beep', payload);

            case 'test_response':
                return this.handleResponse('test', payload);

            case 'get_status_response':
                return this.handleResponse('get_status', payload);

            case 'get_recent_response':
                return this.handleResponse('get_recent', payload);

            default:
                console.warn(`[PiShock] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleShockEvent(payload) {
        console.log('[PiShock] Shock event received:', payload.type);

        // Add to recent events
        this.recentEvents.unshift(payload);
        if (this.recentEvents.length > 100) {
            this.recentEvents.pop();
        }

        this.notifyShockEventListeners(payload);
    }

    handleInitialState(payload) {
        console.log('[PiShock] Received initial state');
        if (payload.configured !== undefined) {
            this.configured = payload.configured;
        }
        if (payload.shockers) {
            this.shockers = payload.shockers;
        }
        if (payload.recentEvents) {
            this.recentEvents = payload.recentEvents;
        }
        this.notifyUpdateListeners();
    }

    handleStatusUpdate(payload) {
        console.log('[PiShock] Status update received');
        if (payload.configured !== undefined) {
            this.configured = payload.configured;
        }
        if (payload.shockers) {
            this.shockers = payload.shockers;
        }
        this.notifyStatusUpdateListeners(payload);
    }

    // =====================================
    // Generic Response Handler
    // =====================================

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // Configuration Methods
    // =====================================

    /**
     * Configure PiShock credentials
     * @param {Object} config - { username, apiKey, name }
     * @returns {Promise<Object>}
     */
    async configure(config) {
        return new Promise((resolve) => {
            this.sendMessage('configure', config);
            this._pendingRequests.set('configure', resolve);
        });
    }

    /**
     * Get current configuration
     * @returns {Promise<Object>}
     */
    async getConfig() {
        return new Promise((resolve) => {
            this.sendMessage('get_config', {});
            this._pendingRequests.set('get_config', resolve);
        });
    }

    // =====================================
    // Shocker Management Methods
    // =====================================

    /**
     * Add a new shocker
     * @param {string} code - Shocker share code
     * @param {string} nickname - Optional nickname
     * @returns {Promise<Object>}
     */
    async addShocker(code, nickname = null) {
        return new Promise((resolve) => {
            this.sendMessage('add_shocker', { code, nickname });
            this._pendingRequests.set('add_shocker', resolve);
        });
    }

    /**
     * Remove a shocker
     * @param {string} code - Shocker share code
     * @returns {Promise<Object>}
     */
    async removeShocker(code) {
        return new Promise((resolve) => {
            this.sendMessage('remove_shocker', { code });
            this._pendingRequests.set('remove_shocker', resolve);
        });
    }

    /**
     * Get all configured shockers
     * @returns {Promise<Object>}
     */
    async getShockers() {
        return new Promise((resolve) => {
            this.sendMessage('get_shockers', {});
            this._pendingRequests.set('get_shockers', resolve);
        });
    }

    /**
     * Get info for a specific shocker
     * @param {string} code - Shocker share code
     * @returns {Promise<Object>}
     */
    async getShockerInfo(code) {
        return new Promise((resolve) => {
            this.sendMessage('get_shocker_info', { code });
            this._pendingRequests.set('get_shocker_info', resolve);
        });
    }

    // =====================================
    // Command Methods
    // =====================================

    /**
     * Send a shock
     * @param {string} code - Shocker share code
     * @param {number} intensity - Intensity 1-100
     * @param {number} duration - Duration 1-15 seconds
     * @returns {Promise<Object>}
     */
    async shock(code, intensity, duration) {
        return new Promise((resolve) => {
            this.sendMessage('shock', { code, intensity, duration });
            this._pendingRequests.set('shock', resolve);
        });
    }

    /**
     * Send a vibration
     * @param {string} code - Shocker share code
     * @param {number} intensity - Intensity 1-100
     * @param {number} duration - Duration 1-15 seconds
     * @returns {Promise<Object>}
     */
    async vibrate(code, intensity, duration) {
        return new Promise((resolve) => {
            this.sendMessage('vibrate', { code, intensity, duration });
            this._pendingRequests.set('vibrate', resolve);
        });
    }

    /**
     * Send a beep
     * @param {string} code - Shocker share code
     * @param {number} duration - Duration 1-15 seconds
     * @returns {Promise<Object>}
     */
    async beep(code, duration) {
        return new Promise((resolve) => {
            this.sendMessage('beep', { code, duration });
            this._pendingRequests.set('beep', resolve);
        });
    }

    /**
     * Send a test vibration
     * @param {string} code - Shocker share code
     * @returns {Promise<Object>}
     */
    async test(code) {
        return new Promise((resolve) => {
            this.sendMessage('test', { code });
            this._pendingRequests.set('test', resolve);
        });
    }

    // =====================================
    // Status Methods
    // =====================================

    /**
     * Get status of all shockers
     * @returns {Promise<Object>}
     */
    async getStatus() {
        return new Promise((resolve) => {
            this.sendMessage('get_status', {});
            this._pendingRequests.set('get_status', resolve);
        });
    }

    /**
     * Get recent shock events
     * @param {number} limit - Max events to return
     * @returns {Promise<Object>}
     */
    async getRecent(limit = 20) {
        return new Promise((resolve) => {
            this.sendMessage('get_recent', { limit });
            this._pendingRequests.set('get_recent', resolve);
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    /**
     * Subscribe to shock events
     * @param {Function} callback - Called with shock event data
     * @returns {Function} Unsubscribe function
     */
    onShockEvent(callback) {
        this.shockEventCallbacks.add(callback);
        return () => this.shockEventCallbacks.delete(callback);
    }

    /**
     * Subscribe to status updates
     * @param {Function} callback - Called with status data
     * @returns {Function} Unsubscribe function
     */
    onStatusUpdate(callback) {
        this.statusUpdateCallbacks.add(callback);
        return () => this.statusUpdateCallbacks.delete(callback);
    }

    /**
     * Subscribe to general updates
     * @param {Function} callback - Called when data changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyShockEventListeners(data) {
        for (const callback of this.shockEventCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[PiShock] Error in shock event callback:', error);
            }
        }
    }

    notifyStatusUpdateListeners(data) {
        for (const callback of this.statusUpdateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[PiShock] Error in status update callback:', error);
            }
        }
    }

    notifyUpdateListeners() {
        const data = {
            configured: this.configured,
            shockers: this.shockers,
            recentEvents: this.recentEvents
        };
        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[PiShock] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    isConfigured() {
        return this.configured;
    }

    getShockersList() {
        return this.shockers;
    }

    getRecentEvents() {
        return this.recentEvents;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[PiShock] Cleaning up...');
        this.shockers = [];
        this.recentEvents = [];
        this.shockEventCallbacks.clear();
        this.statusUpdateCallbacks.clear();
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
